import { useMemo, useState } from "react"
import { useBreite, useTip } from "@/components/Tooltip"
import { Karte, Knopf, Leer } from "@/components/ui"
import { gruppiere, jahrLabel, kennwert, type AuswertungsDaten, type Filter } from "@/lib/auswertung"
import { MONATE, farbe, niceTicks, nf0 } from "@/lib/format"

interface Props {
  d: AuswertungsDaten
  tp: Float64Array
  ids: number[]
  f: Filter
}

interface Punkt { k: number; n: number; v: number | null }

export function Verlauf({ d, tp, ids, f }: Props) {
  const [tabelle, setTabelle] = useState(false)
  const jahre = useMemo(() => [...f.jahre].sort((a, b) => a - b), [f.jahre])

  const daten = useMemo(() => {
    if (!ids.length || !jahre.length) return null
    const j0 = jahre[0]
    const nM = (jahre[jahre.length - 1] - j0 + 1) * 12
    const proMonat = gruppiere(ids, (i) => (d.rows.y[i] - j0) * 12 + d.rows.m[i] - 1)
    const pts: Punkt[] = []
    for (let k = 0; k < nM; k++) {
      const g = proMonat.get(k) ?? []
      pts.push({ k, n: g.length, v: g.length >= 3 ? kennwert(g.map((i) => tp[i]), f.kw) : null })
    }
    const proJahr = gruppiere(ids, (i) => d.rows.y[i])
    const yrs = jahre.map((y) => {
      const g = proJahr.get(y) ?? []
      return { y, n: g.length, v: g.length ? kennwert(g.map((i) => tp[i]), f.kw) : null }
    })
    return { j0, nM, pts, yrs }
  }, [d, tp, ids, jahre, f.kw])

  return (
    <Karte
      titel="Verlauf je Monat"
      unter="Monatswert ab 3 Proben. Der waagerechte Strich zeigt den Jahreswert, der auch unter der Achse steht."
      rechts={
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink2">
          <span className="inline-flex items-center gap-1.5"><i className="inline-block h-0.5 w-4 rounded bg-accent" />Monatswert</span>
          <span className="inline-flex items-center gap-1.5"><i className="inline-block h-0.5 w-4 rounded" style={{ background: farbe("year-line") }} />Jahreswert</span>
          <Knopf variante="link" aria-pressed={tabelle} onClick={() => setTabelle(!tabelle)}>
            {tabelle ? "Als Diagramm" : "Als Tabelle"}
          </Knopf>
        </div>
      }
    >
      {!daten ? <Leer /> : tabelle ? <VerlaufTabelle d={d} {...daten} jahre={jahre} /> : <VerlaufDiagramm d={d} {...daten} />}
    </Karte>
  )
}

function VerlaufTabelle({ d, j0, pts, yrs, jahre }: { d: AuswertungsDaten; j0: number; pts: Punkt[]; yrs: Array<{ y: number; v: number | null }>; jahre: number[] }) {
  return (
    <div className="min-w-0 overflow-x-auto">
      <table className="tabular w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="text-[11.5px] text-muted">
            <th className="px-2 py-1.5 text-left font-medium">Jahr</th>
            {MONATE.map((m) => <th key={m} className="px-2 py-1.5 text-right font-medium">{m}</th>)}
            <th className="px-2 py-1.5 text-right font-medium">Jahr</th>
          </tr>
        </thead>
        <tbody>
          {jahre.map((y) => (
            <tr key={y} className="border-t border-grid">
              <td className="px-2 py-1.5">{jahrLabel(d, y)}</td>
              {MONATE.map((_, m) => {
                const p = pts[(y - j0) * 12 + m]
                return (
                  <td key={m} title={`${p.n} Proben`} className={`px-2 py-1.5 text-right ${p.v == null ? "text-muted" : ""}`}>
                    {p.v == null ? (p.n ? `(${p.n})` : "–") : nf0.format(p.v)}
                  </td>
                )
              })}
              <td className="px-2 py-1.5 text-right">{(() => { const e = yrs.find((x) => x.y === y); return e?.v == null ? "–" : nf0.format(e.v) })()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function VerlaufDiagramm({ d, j0, nM, pts, yrs }: { d: AuswertungsDaten; j0: number; nM: number; pts: Punkt[]; yrs: Array<{ y: number; n: number; v: number | null }> }) {
  const [ref, breite] = useBreite<HTMLDivElement>()
  const tip = useTip()
  const [aktiv, setAktiv] = useState(-1)
  const W = Math.max(300, breite), H = 300
  const m = { l: 46, r: 58, t: 14, b: 50 }
  const pw = W - m.l - m.r, ph = H - m.t - m.b
  const werte = pts.filter((p) => p.v != null).map((p) => p.v as number)
    .concat(yrs.filter((e) => e.v != null).map((e) => e.v as number))
  if (!werte.length) return <div ref={ref}><Leer>Zu wenige Proben je Monat für einen Verlauf. Die Tabelle zeigt die Anzahl.</Leer></div>
  let lo = Math.min(...werte), hi = Math.max(...werte)
  const pad = Math.max(20, (hi - lo) * 0.08)
  const ticks = niceTicks(lo - pad, hi + pad, 5)
  lo = ticks[0]; hi = ticks[ticks.length - 1]
  const x = (k: number) => m.l + ((k + 0.5) / nM) * pw
  const y = (v: number) => m.t + ph - ((v - lo) / (hi - lo)) * ph
  const mitEinheit = (pw * 12) / nM > 80

  let pfad = "", offen = false
  const einzel: Punkt[] = []
  pts.forEach((p, k) => {
    if (p.v == null) { offen = false; return }
    const vorher = k > 0 && pts[k - 1].v != null, nachher = k < nM - 1 && pts[k + 1].v != null
    if (!vorher && !nachher) einzel.push(p)
    pfad += `${offen ? "L" : "M"}${x(k).toFixed(1)},${y(p.v).toFixed(1)}`
    offen = true
  })
  const letzter = [...pts].reverse().find((p) => p.v != null)

  function zeige(k: number, cx?: number, cy?: number) {
    const kk = Math.max(0, Math.min(nM - 1, k))
    setAktiv(kk)
    const p = pts[kk]
    const jahr = j0 + Math.floor(kk / 12)
    const jw = yrs.find((e) => e.y === jahr)
    const r = ref.current?.getBoundingClientRect()
    const zeilen = [
      { label: "Monatswert", farbe: farbe("accent"), wert: p.v == null ? "zu wenige Proben" : `${nf0.format(p.v)} kg/h` },
      { label: "Proben", wert: nf0.format(p.n) },
    ]
    if (jw?.v != null) zeilen.push({ label: `Jahreswert ${jahr}`, farbe: farbe("year-line"), wert: `${nf0.format(jw.v)} kg/h` })
    tip.zeige(cx ?? (r?.left ?? 0) + x(kk), cy ?? (r?.top ?? 0) + (p.v != null ? y(p.v) : m.t + ph / 2), `${MONATE[kk % 12]} ${jahr}`, zeilen)
  }
  function aus() { setAktiv(-1); tip.verstecke() }

  return (
    <div ref={ref} className="relative w-full min-w-0">
      {breite > 0 && (
        <svg
          width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" tabIndex={0} className="block overflow-visible"
          aria-label={`Durchsatz je Monat, ${yrs[0].y} bis ${yrs[yrs.length - 1].y}. Pfeiltasten wählen einen Monat.`}
          onKeyDown={(e) => {
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return
            e.preventDefault()
            zeige(aktiv < 0 ? nM - 1 : aktiv + (e.key === "ArrowRight" ? 1 : -1))
          }}
          onBlur={aus}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={m.l} x2={W - m.r} y1={y(t)} y2={y(t)} stroke={farbe("grid")} strokeWidth={1} />
              <text x={m.l - 8} y={y(t) + 4} textAnchor="end" className="fill-muted text-[11px] tabular">{nf0.format(t)}</text>
            </g>
          ))}
          <text x={m.l - 8} y={m.t - 2} textAnchor="end" className="fill-muted text-[11px]">kg/h</text>
          {yrs.map((e, j) => {
            const xa = m.l + ((j * 12) / nM) * pw, xb = m.l + (((j + 1) * 12) / nM) * pw
            return (
              <g key={e.y}>
                {j > 0 && <line x1={xa} x2={xa} y1={m.t} y2={m.t + ph} stroke={farbe("axis")} strokeWidth={1} />}
                <text x={(xa + xb) / 2} y={H - m.b + 18} textAnchor="middle" className="fill-ink2 text-[11.5px] font-medium">
                  {mitEinheit ? jahrLabel(d, e.y) : String(e.y)}
                </text>
                <text x={(xa + xb) / 2} y={H - m.b + 34} textAnchor="middle" className="fill-muted text-[11px] tabular">
                  {e.v == null ? "–" : `${nf0.format(e.v)}${mitEinheit ? " kg/h" : ""}`}
                </text>
                {e.v != null && (
                  <line x1={xa + 3} x2={xb - 3} y1={y(e.v)} y2={y(e.v)} stroke={farbe("year-line")} strokeWidth={2} strokeLinecap="round" opacity={0.55} />
                )}
              </g>
            )
          })}
          <line x1={m.l} x2={W - m.r} y1={m.t + ph} y2={m.t + ph} stroke={farbe("axis")} strokeWidth={1} />
          <path d={pfad} fill="none" stroke={farbe("accent")} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          {einzel.map((p) => <circle key={p.k} cx={x(p.k)} cy={y(p.v as number)} r={3} fill={farbe("accent")} />)}
          {letzter && (
            <>
              <circle cx={x(letzter.k)} cy={y(letzter.v as number)} r={4} fill={farbe("accent")} stroke={farbe("surface")} strokeWidth={2} />
              <text x={x(letzter.k) + 8} y={y(letzter.v as number) + 4} className="fill-ink text-xs font-medium tabular">{nf0.format(letzter.v as number)}</text>
            </>
          )}
          {aktiv >= 0 && (
            <>
              <line x1={x(aktiv)} x2={x(aktiv)} y1={m.t} y2={m.t + ph} stroke={farbe("ink-2")} strokeWidth={1} />
              {pts[aktiv].v != null && (
                <circle cx={x(aktiv)} cy={y(pts[aktiv].v as number)} r={4.5} fill={farbe("accent")} stroke={farbe("surface")} strokeWidth={2} />
              )}
            </>
          )}
          <rect
            x={m.l} y={m.t} width={pw} height={ph} fill="transparent"
            onPointerMove={(e) => {
              const r = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect()
              zeige(Math.floor(((e.clientX - r.left - m.l) / pw) * nM), e.clientX, e.clientY)
            }}
            onPointerLeave={aus}
          />
        </svg>
      )}
    </div>
  )
}
